// TransferPresidencyCard.jsx — Écran dédié de transfert de présidence (web).
// Le président choisit un membre qui devient président ; lui devient vice-président.
import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Crown, Loader2, ArrowRightLeft, AlertTriangle } from 'lucide-react';

export default function TransferPresidencyCard() {
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get('/assos/users')
      .then(({ data }) => {
        if (!alive) return;
        setMembers(data.filter((u) => u.role !== 'president' && u.actif !== false && !u.compte_supprime));
      })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const submit = async () => {
    if (!selected) return toast.error('Choisissez le nouveau président');
    setSubmitting(true);
    try {
      await api.patch(`/assos/users/${selected}/role`, { role: 'president' });
      toast.success('Présidence transférée. Vous êtes désormais vice-président·e.');
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors du transfert');
      setSubmitting(false);
    }
  };

  const selectedName = () => {
    const m = members.find((x) => x.id === selected);
    return m ? `${m.prenom || ''} ${m.nom || ''}`.trim() || m.email : '';
  };

  return (
    <div className="card mb-8 border border-[#FEF3C7] bg-[#FFFBEB]/60" data-testid="transfer-presidency-card">
      <div className="flex items-center gap-2 mb-2">
        <Crown className="w-5 h-5 text-[#B45309]" />
        <h2 className="font-display text-lg font-bold text-[#0F172A]">Transférer la présidence</h2>
      </div>
      <p className="text-sm text-[#475569] mb-4 max-w-2xl">
        Confiez la présidence à un membre sans supprimer votre compte. Le membre choisi devient président·e,
        et vous devenez automatiquement vice-président·e.
      </p>

      {loading ? (
        <div className="py-4"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : members.length === 0 ? (
        <div className="text-sm text-[#475569] bg-white rounded-xl p-4 border border-[#F1F5F9]">
          Aucun autre membre disponible. Invitez d'abord un membre pour pouvoir lui transférer la présidence.
        </div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <label className="label-uppercase block mb-1.5">Nouveau président·e</label>
            <select
              className="ap-input"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              data-testid="transfer-presidency-select"
            >
              <option value="">— Choisir un membre —</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {`${m.prenom || ''} ${m.nom || ''}`.trim()} {m.email ? `(${m.email})` : ''} — {(m.role || 'membre').replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => (selected ? setConfirm(true) : toast.error('Choisissez un membre'))}
            className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
            data-testid="transfer-presidency-open"
          >
            <ArrowRightLeft className="w-4 h-4" /> Transférer
          </button>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-[90] bg-[#0F172A]/60 backdrop-blur-md grid place-items-center p-4" onClick={() => !submitting && setConfirm(false)}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7" onClick={(e) => e.stopPropagation()} data-testid="transfer-presidency-modal">
            <div className="w-12 h-12 rounded-2xl bg-[#FFFBEB] grid place-items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-[#B45309]" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#0F172A] mb-2">Confirmer le transfert</h3>
            <p className="text-sm text-[#475569] mb-6">
              <strong className="text-[#0F172A]">{selectedName()}</strong> deviendra président·e de l'association.
              Vous deviendrez vice-président·e. Cette action prend effet immédiatement.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirm(false)} disabled={submitting} className="btn-secondary" data-testid="transfer-presidency-cancel">Annuler</button>
              <button onClick={submit} disabled={submitting} className="btn-primary inline-flex items-center gap-2" data-testid="transfer-presidency-confirm">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Confirmer le transfert
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
